import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * dm-room — the pup's room cross-sell (snippets/dm-room.liquid).
 *
 * A SUBSCRIBER of the buy form: <dm-personalizer> and <dm-standard-form> publish `dm:state`, the
 * legacy product-standard form publishes the theme's `variant:update`. It owns only which tiles are
 * picked. Whoever submits pulls the extra lines from getItems(), and both DM forms read the total
 * from `dm:addons-change`, the same contract as dm-addons.
 *
 * products mode: a tile resolves to the variant closest to the main item's size. Clothes and
 * leashes hide a piece not made in that size; plush keep showing with their default size. A
 * colorway tile stays on its own color, and a personalized tile hides when it does not offer the
 * fabric chosen on the main item.
 * list mode (seasonal bows): a tile is one listed product, sold as shown, so its line carries no
 * name or initials. Its photo is a scroll-snap strip of its available variants, and the style
 * showing is the one it sells: a swipe on a picked tile changes its line, an unpicked one only
 * previews. Its dots and arrows send no change event, so the main item's own options never hear them.
 * Each photo that swipes nudges once, the first time the room is in view.
 * colors mode (Dog Hair Bows with no list): a tile is the same style in another color, with the
 * same initials, and the main item's own color steps aside.
 */

const CLOTHING_SIZES = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl'];
const SIZE_WORDS = { small: 's', medium: 'm', large: 'l' };
const MAX_TILES = 5;
const BUY_FORMS = 'dm-personalizer, dm-standard-form';
/** A pointer that travels this far, or a strip that scrolls under it, swiped and did not tap. */
const TAP_SLOP = 8;
/** The swipe hint starts this long after the room comes into view, one photo after another. */
const HINT_DELAY = 500;
const HINT_STAGGER = 180;
/** How long the hint waits for the next style's photo before it gives up. */
const HINT_LOAD_WAIT = 2000;

/**
 * "Small" and "S (9 x 4)" → ['s'], "S/M" → ['s', 'm']. Anything else, such as "4 ft", is not a
 * clothing size and returns [], so it never filters a tile out.
 * @param {string} label
 * @returns {string[]}
 */
function sizeKeys(label) {
  const head = String(label || '').toLowerCase().trim().split(/[\s(]/)[0] || '';
  const keys = head.split('/').map((part) => SIZE_WORDS[part] || part);
  return keys.every((key) => CLOTHING_SIZES.includes(key)) ? keys : [];
}

/** @param {string[]} names */
function sentence(names) {
  if (names.length < 2) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * The same key Liquid's handleize gives the fabric and thread labels.
 * @param {string} key
 */
function colorKey(key) {
  return key.toLowerCase().trim().replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

class DmRoom extends HTMLElement {
  /** @type {any[]} */
  #mainVariants = [];
  #variantId = '';
  #petName = '';
  #noName = false;
  #nameIsInitials = false;
  /** Toggled "Pair it with" cards on the standard form, so the tray's total matches its CTA. */
  #formCents = 0;
  /** @type {IntersectionObserver | null} */
  #hintObserver = null;
  #colors = { fabricLabel: '', threadLabel: '', threadSwatch: '' };

  #onState = (/** @type {any} */ event) => {
    const detail = event.detail;
    if (!detail || typeof detail.petName !== 'string') return;
    // A quick add elsewhere on the page is a different product.
    const form = event.target;
    if (form instanceof HTMLElement && form.dataset.productId && form.dataset.productId !== this.dataset.productId) return;
    this.#readState(detail);
    this.#render();
  };

  #onVariant = (/** @type {any} */ event) => {
    const data = event.detail?.data;
    if (data?.productId && String(data.productId) !== this.dataset.productId) return;
    const id = event.detail?.resource?.id || this.#variantFromPicker(event.target);
    if (!id) return;
    this.#variantId = String(id);
    this.#render();
  };

  /**
   * Same match as product-form.js: the picker's checked values, in option order.
   * @param {EventTarget | null} target
   */
  #variantFromPicker(target) {
    if (!(target instanceof Element)) return '';
    const values = Array.from(target.querySelectorAll('select option[selected], fieldset input:checked')).map(
      (option) => /** @type {HTMLInputElement} */ (option).value
    );
    const match = this.#mainVariants.find(
      (v) => v.options.length === values.length && v.options.every((/** @type {string} */ value, /** @type {number} */ i) => value === values[i])
    );
    return match ? match.id : '';
  }

  #onCart = (/** @type {any} */ event) => {
    const data = event.detail?.data;
    if (data?.didError) return;
    if (!['product-form-component', 'sticky-add-to-cart', 'dm-personalizer', 'dm-standard-form'].includes(data?.source)) return;
    for (const item of this.#items()) item.__picked = false;
    this.#render();
  };

  connectedCallback() {
    this.#mainVariants = this.#json(this.querySelector('[data-dm-room-main]'), []);
    this.#variantId = this.dataset.initialVariant || '';

    for (const item of this.#items()) {
      Object.assign(item, {
        __picked: false,
        __variants: this.#json(item.querySelector('[data-dm-room-variants]'), []),
        __colors: this.#json(item.querySelector('[data-dm-room-colors]'), { fabrics: {}, threads: {} }),
        __variant: null,
        __size: '',
        __index: 0,
        __seek: -1,
        __down: null,
        __touched: false,
      });
      item.addEventListener('click', this.#toggle);
      if (this.dataset.mode === 'list') this.#bindStrip(item);
    }
    if (this.dataset.mode === 'list') this.#hintWhenSeen();

    document.addEventListener('dm:state', this.#onState);
    const controller = /** @type {any} */ (this.closest(BUY_FORMS) || document.querySelector(BUY_FORMS));
    if (controller?.snapshot && controller.dataset.productId === this.dataset.productId) {
      this.#readState({ ...controller.snapshot });
    }
    if (this.dataset.personalized !== 'true') {
      document.addEventListener(ThemeEvents.variantUpdate, this.#onVariant);
    }
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCart);
    this.#render();
  }

  disconnectedCallback() {
    this.#hintObserver?.disconnect();
    document.removeEventListener('dm:state', this.#onState);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariant);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCart);
  }

  /** @param {any} detail */
  #readState(detail) {
    this.#petName = detail.petName || '';
    this.#noName = detail.noName === true;
    this.#nameIsInitials = detail.nameIsInitials === true;
    this.#formCents = Number(detail.upsellCents) || 0;
    if (detail.variantId) this.#variantId = String(detail.variantId);
    const text = (/** @type {unknown} */ value) => (typeof value === 'string' ? value : '');
    this.#colors = {
      fabricLabel: text(detail.toyColorLabel),
      threadLabel: text(detail.embroideryLabel),
      threadSwatch: text(detail.embroideryUrl),
    };
  }

  #toggle = (/** @type {MouseEvent} */ event) => {
    const item = /** @type {any} */ (event.currentTarget);
    if (this.dataset.mode === 'list' && !this.#listTap(item, event)) return;
    item.__picked = !item.__picked;
    this.#error('');
    this.#render();
  };

  /**
   * True when a click on a list tile picks it: a tap on the photo or on the text. A dot or an arrow
   * moves the strip instead, and a click that ends a swipe is not a tap. A finger's swipe sends no
   * click, but a mouse drag does.
   * @param {any} item
   * @param {MouseEvent} event
   */
  #listTap(item, event) {
    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest('[data-dm-room-dot], [data-dm-room-step]');
    if (control instanceof HTMLElement) {
      if (control.dataset.dmRoomDot !== undefined) this.#goTo(item, Number(control.dataset.dmRoomDot));
      else this.#goTo(item, item.__index + Number(control.dataset.dmRoomStep), true);
      return false;
    }
    if (target?.closest('[data-dm-room-pick]')) return true;
    if (!target?.closest('[data-dm-room-strip]')) return false;

    const down = item.__down;
    item.__down = null;
    if (!down) return true;
    const travel = Math.hypot(event.clientX - down.x, event.clientY - down.y);
    return travel <= TAP_SLOP && Math.abs(item.__strip.scrollLeft - down.left) <= 2;
  }

  /**
   * List mode: the strip's swipe, keys and mouse drag. A tile with one style has nothing to move.
   * @param {any} item
   */
  #bindStrip(item) {
    const strip = item.querySelector('[data-dm-room-strip]');
    item.__strip = strip;
    if (!(strip instanceof HTMLElement) || item.__variants.length < 2) return;

    // The frames past the first are lazy, so load them once a swipe looks likely.
    const warm = () => {
      for (const image of strip.querySelectorAll('img[loading="lazy"]')) /** @type {HTMLImageElement} */ (image).loading = 'eager';
    };
    for (const type of ['pointerenter', 'focusin', 'scroll']) item.addEventListener(type, warm, { once: true, capture: true });

    strip.addEventListener('scroll', () => this.#onStripScroll(item), { passive: true });
    strip.addEventListener('scrollend', () => this.#settle(item));
    // On the whole tile, so the keys still work after a click on an arrow or a dot. Not on the pick.
    item.addEventListener('keydown', (/** @type {KeyboardEvent} */ event) => {
      if (event.target instanceof Element && !event.target.closest('[data-dm-room-pick]')) this.#onStripKey(item, event);
    });
    strip.addEventListener('pointerdown', (event) => this.#onStripDown(item, event));
  }

  /**
   * List mode, once a page: when the row of tiles is first in view, each photo that swipes slides a
   * little way towards its next style and back, so it reads as more than one still photo. Not with
   * reduced motion, and never on a strip someone has already moved.
   */
  #hintWhenSeen() {
    const items = this.#items().filter((item) => item.__variants.length > 1 && item.__strip instanceof HTMLElement);
    if (!items.length || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#hintObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#hintObserver?.disconnect();
        items.forEach((item, i) => setTimeout(() => this.#hint(item), HINT_DELAY + i * HINT_STAGGER));
      },
      { threshold: 0.6 }
    );
    this.#hintObserver.observe(this.querySelector('.dm-room__list') || this);
  }

  /**
   * The next style's photo is lazy, so the hint waits for it: an empty edge would look broken.
   * @param {any} item
   */
  async #hint(item) {
    const strip = item.__strip;
    const next = strip.children[1];
    if (next instanceof HTMLImageElement && !(next.complete && next.naturalWidth)) {
      next.loading = 'eager';
      await Promise.race([next.decode().catch(() => {}), new Promise((done) => setTimeout(done, HINT_LOAD_WAIT))]);
      if (!(next.complete && next.naturalWidth)) return;
    }
    if (item.__touched || item.hidden) return;
    strip.addEventListener('animationend', () => strip.classList.remove('is-hinting'), { once: true });
    strip.classList.add('is-hinting');
  }

  /**
   * A shopper moved this strip, so its hint has nothing left to say, and one playing now stops.
   * @param {any} item
   */
  #touch(item) {
    item.__touched = true;
    item.__strip?.classList.remove('is-hinting');
  }

  /**
   * The style follows the frame nearest the middle as the strip moves, so the name and the tray
   * change mid-swipe. While a dot or an arrow scrolls the strip, frames passed on the way are skipped.
   * @param {any} item
   */
  #onStripScroll(item) {
    const strip = item.__strip;
    // The hint moves the frames, not the scroll, so this is always the shopper or #goTo.
    this.#touch(item);
    // Safari has no scrollend yet, so a short quiet spell stands in for it.
    if (!('onscrollend' in window)) {
      clearTimeout(item.__settleTimer);
      item.__settleTimer = setTimeout(() => this.#settle(item), 150);
    }
    const index = this.#frameAt(strip);
    if (item.__seek >= 0 && index !== item.__seek) return;
    item.__seek = -1;
    this.#setIndex(item, index);
  }

  /** @param {any} item */
  #settle(item) {
    clearTimeout(item.__settleTimer);
    item.__seek = -1;
    item.__strip.classList.remove('is-settling');
    this.#setIndex(item, this.#frameAt(item.__strip));
  }

  /** @param {HTMLElement} strip */
  #frameAt(strip) {
    return strip.clientWidth ? Math.round(Math.abs(strip.scrollLeft) / strip.clientWidth) : 0;
  }

  /**
   * Arrow keys move one style, Home and End go to the ends.
   * @param {any} item
   * @param {KeyboardEvent} event
   */
  #onStripKey(item, event) {
    const last = item.__variants.length - 1;
    const steps = { ArrowLeft: item.__index - 1, ArrowRight: item.__index + 1, Home: 0, End: last };
    // Alt or Cmd with an arrow is the browser's Back and Forward, never a style.
    if (!(event.key in steps) || event.altKey || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.#goTo(item, steps[/** @type {keyof typeof steps} */ (event.key)], event.key.startsWith('Arrow'));
  }

  /**
   * Notes where a tap began, and lets a mouse drag the strip as a finger would.
   * @param {any} item
   * @param {PointerEvent} event
   */
  #onStripDown(item, event) {
    const strip = item.__strip;
    const start = { x: event.clientX, y: event.clientY, left: strip.scrollLeft };
    item.__down = start;
    item.__seek = -1;
    this.#touch(item);
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    let dragging = false;
    const move = (/** @type {PointerEvent} */ moveEvent) => {
      if (moveEvent.pointerId !== event.pointerId) return;
      // A release the page never heard, such as one outside the window, ends the drag here.
      if (!(moveEvent.buttons & 1)) return up(moveEvent);
      const dx = moveEvent.clientX - start.x;
      if (!dragging && Math.abs(dx) <= TAP_SLOP) return;
      if (!dragging) {
        dragging = true;
        strip.setPointerCapture(moveEvent.pointerId);
        strip.classList.add('is-dragging');
      }
      strip.scrollLeft = start.left - dx;
    };
    const up = (/** @type {PointerEvent} */ upEvent) => {
      if (upEvent.pointerId !== event.pointerId) return;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (!dragging) return;
      // A fifth of a frame is enough to mean the next one.
      const dx = upEvent.clientX - start.x;
      const step = Math.abs(dx) > strip.clientWidth / 5 ? -Math.sign(dx) : 0;
      strip.classList.replace('is-dragging', 'is-settling');
      const next = this.#goTo(item, Math.round(start.left / (strip.clientWidth || 1)) + step);
      // #settle turns the snap back on once the strip stops, or now if it has nowhere to go.
      if (Math.abs(strip.scrollLeft - next * strip.clientWidth) < 1) this.#settle(item);
    };
    // On the window, so a press that leaves the strip before it drags, or lets go outside it, still
    // ends here; on the strip those listeners outlived the press and a later hover dragged it.
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }

  /**
   * Scroll a list tile to a style, from a dot, an arrow, a key or a drag. Arrows go round.
   * @param {any} item
   * @param {number} index
   * @param {boolean} [wrap]
   * @returns {number} the style it went to
   */
  #goTo(item, index, wrap = false) {
    const count = item.__variants.length;
    const strip = item.__strip;
    if (count < 2 || !(strip instanceof HTMLElement)) return item.__index;
    this.#touch(item);
    const next = wrap ? (index + count) % count : Math.min(Math.max(index, 0), count - 1);
    // Armed only when the strip will move: with no scroll to clear it, it would mute the next swipe.
    item.__seek = Math.abs(strip.scrollLeft - next * strip.clientWidth) < 1 ? -1 : next;
    // CSS makes this smooth, or instant with reduced motion.
    strip.scrollTo({ left: next * strip.clientWidth });
    this.#setIndex(item, next);
    return next;
  }

  /**
   * @param {any} item
   * @param {number} index
   */
  #setIndex(item, index) {
    const next = Math.min(Math.max(index, 0), item.__variants.length - 1);
    if (next === item.__index) return;
    item.__index = next;
    this.#render();
  }

  /** @returns {any[]} */
  #items() {
    return Array.from(this.querySelectorAll('[data-dm-room-item]'));
  }

  /**
   * @param {Element | null} script
   * @param {any} fallback
   */
  #json(script, fallback) {
    try {
      return script?.textContent ? JSON.parse(script.textContent) : fallback;
    } catch (error) {
      console.error('[dm-room] Payload is not valid JSON.', error);
      return fallback;
    }
  }

  #mainVariant() {
    return this.#mainVariants.find((v) => String(v.id) === this.#variantId) || null;
  }

  #mainSize() {
    const index = Number(this.dataset.sizeIndex);
    return index >= 0 ? this.#mainVariant()?.options?.[index] || '' : '';
  }

  /**
   * The closest available variant for a product tile, or null when a size-led family should hide it.
   * @param {any} item
   * @param {string[]} wanted
   */
  #resolveProduct(item, wanted) {
    if (!this.#offersFabric(item)) return null;

    let variants = item.__variants.filter((/** @type {any} */ v) => v.available);
    const ownColor = item.dataset.ownColor;
    if (ownColor) {
      const colorIndex = Number(item.dataset.colorIndex);
      variants = variants.filter((/** @type {any} */ v) => v.options?.[colorIndex] === ownColor);
    }
    if (!variants.length) return null;

    const fallback = variants.find((/** @type {any} */ v) => String(v.id) === item.dataset.defaultVariant) || variants[0];
    const index = Number(item.dataset.sizeIndex);
    if (index < 0 || !wanted.length) return fallback;

    const sized = variants
      .map((/** @type {any} */ v) => ({ v, keys: sizeKeys(v.options?.[index]) }))
      .filter((/** @type {any} */ entry) => entry.keys.length > 0);
    if (!sized.length) return fallback;

    const matches = sized
      .filter((/** @type {any} */ entry) => entry.keys.some((/** @type {string} */ key) => wanted.includes(key)))
      .sort(
        (/** @type {any} */ a, /** @type {any} */ b) =>
          a.keys.length - b.keys.length || Number(wanted.includes(b.keys[0])) - Number(wanted.includes(a.keys[0]))
      );
    if (matches.length) return matches[0].v;
    return this.dataset.sizeLed === 'true' || ownColor ? null : fallback;
  }

  /**
   * False when the chosen fabric is not on this personalized product's list (owner 2026-11-02). A
   * product with no fabric list has nothing to clash with: it keeps its tile and its line carries
   * no fabric, see #colorsFor.
   * @param {any} item
   */
  #offersFabric(item) {
    if (item.dataset.personalizable !== 'true' || !this.#colors.fabricLabel) return true;
    const own = item.__colors?.fabrics || {};
    return !Object.keys(own).length || Boolean(own[colorKey(this.#colors.fabricLabel)]);
  }

  /**
   * The style showing on a list tile's strip. Liquid wrote only available styles, one per frame.
   * @param {any} item
   */
  #resolveList(item) {
    return item.__variants[item.__index] || item.__variants[0] || null;
  }

  /**
   * The same style in this tile's color.
   * @param {any} item
   */
  #resolveColor(item) {
    const main = this.#mainVariant();
    const colorIndex = Number(this.dataset.colorIndex);
    if (!main || colorIndex < 0) return null;
    if (main.options[colorIndex] === item.dataset.color) return null;

    return (
      this.#mainVariants.find(
        (v) =>
          v.available &&
          v.options.every(
            (/** @type {string} */ value, /** @type {number} */ index) =>
              index === colorIndex ? value === item.dataset.color : value === main.options[index]
          )
      ) || null
    );
  }

  #render() {
    const mode = this.dataset.mode;
    const colorsMode = mode === 'colors';
    const listMode = mode === 'list';
    const mainSize = this.#mainSize();
    const wanted = sizeKeys(mainSize);
    const sizeIndexOwn = Number(this.dataset.sizeIndex);
    let visible = 0;

    for (const item of this.#items()) {
      let variant = listMode ? this.#resolveList(item) : colorsMode ? this.#resolveColor(item) : this.#resolveProduct(item, wanted);
      // Liquid renders spares, so a size that hides some still fills the row. A list shows them all.
      if (mode === 'products' && visible >= MAX_TILES) variant = null;
      item.__variant = variant;
      item.hidden = !variant;
      if (!variant) item.__picked = false;
      else visible += 1;
      // A list tile's button is its text; every other tile is a button itself.
      const pick = item.querySelector('[data-dm-room-pick]') || item;
      pick.setAttribute('aria-pressed', String(Boolean(item.__picked)));
      item.toggleAttribute('data-picked', Boolean(item.__picked));

      const price = item.querySelector('[data-dm-room-price]');
      if (variant && price instanceof HTMLElement) {
        const index = colorsMode ? -1 : Number(item.dataset.sizeIndex);
        const sizes = index >= 0 ? new Set(item.__variants.map((/** @type {any} */ v) => v.options?.[index])) : new Set();
        const label = index >= 0 ? String(variant.options?.[index] || '') : '';
        item.__size = label;
        price.textContent = sizes.size > 1 ? `${variant.priceFormatted} · ${label.split(' (')[0]}` : variant.priceFormatted;
      }
      if (listMode && variant) this.#renderListTile(item, variant);
    }

    this.hidden = visible === 0;
    this.#renderHeading(mainSize, sizeIndexOwn);
    this.#renderTray(colorsMode, mainSize);
    this.#publish();
  }

  /**
   * The style showing: its name (a polite live region, so it is only written when it changes), its
   * dot, or its count past six styles.
   * @param {any} item
   * @param {any} variant
   */
  #renderListTile(item, variant) {
    const chosen = item.querySelector('[data-dm-room-chosen]');
    if (chosen && chosen.textContent !== variant.name) chosen.textContent = variant.name;
    for (const dot of item.querySelectorAll('[data-dm-room-dot]')) {
      if (Number(dot.dataset.dmRoomDot) === item.__index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
    const position = item.querySelector('[data-dm-room-pos]');
    if (position) position.textContent = `${item.__index + 1}/${item.__variants.length}`;
  }

  /**
   * @param {string} mainSize
   * @param {number} sizeIndex
   */
  #renderHeading(mainSize, sizeIndex) {
    const dream = this.querySelector('[data-dm-room-dream]');
    const sub = this.querySelector('[data-dm-room-sub]');
    if (!dream || !sub) return;

    // Liquid wrote what a list is, and nothing here changes it.
    if (this.dataset.mode === 'list') return;

    if (this.dataset.mode === 'colors') {
      dream.textContent = 'A bow for every mood.';
      sub.textContent = this.#noName ? 'Same style, just another color.' : 'Same initials, just another color.';
      for (const letter of this.querySelectorAll('[data-dm-room-letter]')) {
        letter.textContent = this.#noName ? '' : this.#petName.charAt(0);
      }
      return;
    }

    if (this.dataset.sizeLed === 'true' && sizeIndex >= 0 && sizeKeys(mainSize).length) {
      dream.textContent = `Also comes in ${mainSize}.`;
      sub.textContent = 'Only pieces made in this size show up here.';
      return;
    }

    dream.textContent = `${this.#pretty() || 'Your pup'} is daydreaming about…`;
    const shown = this.#items().filter((item) => !item.hidden);
    const stitched = shown.length > 0 && shown.every((item) => item.dataset.personalizable === 'true');
    sub.textContent = stitched && this.#petName ? `Each one comes with ${this.#petName} stitched on too.` : '';
  }

  #pretty() {
    const name = this.#petName;
    return name && !this.#isInitials() ? name.charAt(0) + name.slice(1).toLowerCase() : '';
  }

  #isInitials() {
    return this.dataset.mode === 'colors' || this.#nameIsInitials;
  }

  /** The short name up to its comma, plus the color chosen now: a colorway product sells them all. */
  #mainName() {
    const name = this.dataset.mainName || '';
    const color = this.#mainVariant()?.options?.[Number(this.dataset.colorIndex)];
    return color ? `${name}, ${color}` : name;
  }

  /**
   * "Christmas Hair Bows, Santa Charm", or just the product when it has one unnamed variant.
   * @param {any} item
   */
  #listLabel(item) {
    const style = item.__variant?.name;
    return style ? `${item.dataset.title}, ${style}` : item.dataset.title;
  }

  /**
   * @param {boolean} colorsMode
   * @param {string} mainSize
   */
  #renderTray(colorsMode, mainSize) {
    const picked = this.#items().filter((item) => item.__picked && item.__variant);
    const count = picked.length + 1;
    const noun = this.dataset.noun || 'piece';
    const pretty = this.#pretty();

    const title = this.querySelector('[data-dm-room-title]');
    if (title) title.textContent = `${pretty ? `${pretty}'s` : "Your pup's"} ${this.dataset.place || ''}`;

    const counter = this.querySelector('[data-dm-room-count]');
    if (counter) counter.textContent = `${count} ${noun}${count === 1 ? '' : 's'}`;

    const slot = this.querySelector('[data-dm-room-slot]');
    if (slot instanceof HTMLElement) slot.hidden = picked.length > 0;

    this.#renderThumbs(picked, colorsMode);

    const line = this.querySelector('[data-dm-room-line]');
    if (line) {
      if (!picked.length) {
        line.textContent = this.dataset.empty || '';
      } else if (colorsMode) {
        const colorIndex = Number(this.dataset.colorIndex);
        const main = this.#mainVariant()?.options?.[colorIndex] || '';
        const list = sentence([main, ...picked.map((item) => item.dataset.short)]);
        line.textContent = this.#noName || !this.#petName ? `${list}.` : `${list}, with ${this.#petName}.`;
      } else if (this.dataset.mode === 'list') {
        const main = `${this.#mainName()}${this.#petName ? ` with ${this.#petName}` : ''}`;
        // Joined by "and" alone, because each label has its own comma.
        line.textContent = `${main} plus ${picked.map((item) => this.#listLabel(item)).join(' and ')}.`;
      } else if (this.dataset.sizeLed === 'true') {
        const parts = [`${this.#mainName()}${mainSize ? ` in ${mainSize}` : ''}`];
        for (const item of picked) parts.push(`${item.dataset.short}${item.__size ? ` in ${item.__size}` : ''}`);
        line.textContent = `${sentence(parts)}.`;
      } else {
        const list = sentence([this.#mainName(), ...picked.map((item) => item.dataset.short)]);
        const stitched = this.#petName && picked.every((item) => item.dataset.personalizable === 'true');
        line.textContent = stitched ? `${list}, all stitched ${this.#petName}.` : `${list}.`;
      }
    }

    // The personalized form has its own Total row, the standard and legacy forms have none here.
    const total = this.querySelector('[data-dm-room-total]');
    if (total instanceof HTMLElement) {
      const main = this.#mainVariant();
      const show = this.dataset.personalized !== 'true' && picked.length > 0 && Boolean(main);
      total.hidden = !show;
      if (show) total.textContent = `Total ${this.#money(Number(main.price) + this.#formCents + this.getTotalCents())}`;
    }
  }

  /**
   * @param {any[]} picked
   * @param {boolean} colorsMode
   */
  #renderThumbs(picked, colorsMode) {
    const stack = this.querySelector('[data-dm-room-stack]');
    const slot = this.querySelector('[data-dm-room-slot]');
    if (!stack) return;

    // The main thumb shows the variant chosen now, as the tray's text does.
    const mainPhoto = this.querySelector('[data-dm-room-main-thumb] img');
    if (mainPhoto instanceof HTMLImageElement) {
      mainPhoto.dataset.featured ||= mainPhoto.getAttribute('src') || '';
      const src = this.#mainVariant()?.image || mainPhoto.dataset.featured;
      if (mainPhoto.getAttribute('src') !== src) mainPhoto.setAttribute('src', src);
    }

    if (colorsMode) {
      const colorIndex = Number(this.dataset.colorIndex);
      const mainColor = this.#mainVariant()?.options?.[colorIndex];
      const source = this.#items().find((item) => item.dataset.color === mainColor);
      const bow = source?.querySelector('.dm-room__art')?.style.getPropertyValue('--dm-bow');
      const art = this.querySelector('[data-dm-room-main-art]');
      if (bow && art instanceof HTMLElement) art.style.setProperty('--dm-bow', bow);
    }

    // A list tile keeps one thumb, and a swipe on it swaps the thumb's photo without a new drop.
    const listMode = this.dataset.mode === 'list';
    const thumbKey = (/** @type {any} */ item) => (listMode ? item.dataset.productId : item.dataset.short);
    const wanted = picked.map(thumbKey);
    for (const thumb of stack.querySelectorAll('[data-dm-room-thumb]')) {
      const key = /** @type {HTMLElement} */ (thumb).dataset.dmRoomThumb;
      if (!wanted.includes(key)) thumb.remove();
    }
    picked.forEach((item, i) => {
      const key = thumbKey(item);
      const thumbs = Array.from(stack.querySelectorAll('[data-dm-room-thumb]'));
      let thumb = thumbs.find((t) => t.dataset.dmRoomThumb === key);
      if (!thumb) {
        thumb = document.createElement('span');
        thumb.className = 'dm-room__thumb';
        thumb.dataset.dmRoomThumb = key;
        const art = listMode ? this.#listArt() : item.querySelector('.dm-room__art')?.cloneNode(true);
        const check = item.querySelector('.dm-room__check')?.cloneNode(true);
        if (art) thumb.append(art);
        if (check) thumb.append(check);
        // In tile order, as the line reads: before the next picked tile's thumb. A thumb already in
        // place never moves, because moving it would replay its drop.
        const later = picked.slice(i + 1).map(thumbKey);
        stack.insertBefore(thumb, thumbs.find((t) => later.includes(t.dataset.dmRoomThumb || '')) || slot);
      }
      const photo = listMode ? thumb.querySelector('img') : null;
      const src = item.__variant.image;
      if (photo && src && photo.getAttribute('src') !== src) photo.setAttribute('src', src);
    });
  }

  /** An empty photo for a list tile's thumb; #renderThumbs fills it with the style showing. */
  #listArt() {
    const art = document.createElement('span');
    art.className = 'dm-room__art';
    const photo = document.createElement('img');
    Object.assign(photo, { alt: '', width: 56, height: 56 });
    art.append(photo);
    return art;
  }

  /** @param {any} item */
  #colorsFor(item) {
    const own = item.__colors || { fabrics: {}, threads: {} };
    const { fabricLabel, threadLabel, threadSwatch } = this.#colors;
    const fabricSwatch = fabricLabel ? own.fabrics?.[colorKey(fabricLabel)] : '';
    return {
      // Never a fabric this product does not offer.
      fabric: fabricSwatch ? { label: fabricLabel, swatch: fabricSwatch } : null,
      thread: threadLabel ? { label: threadLabel, swatch: own.threads?.[colorKey(threadLabel)] || threadSwatch } : null,
    };
  }

  /**
   * Line items for the picked tiles, ready for the cart. `_title` and `_cardId` are for error
   * reporting and stripped before the request, as with dm-addons.
   * @param {string} [parentName]
   */
  getItems(parentName = '') {
    /** @type {any[]} */
    const items = [];

    for (const item of this.#items()) {
      if (!item.__picked || !item.__variant) continue;

      /** @type {Record<string, string>} */
      const properties = {};
      const listed = this.dataset.mode === 'list';
      const parent = parentName || this.#petName;
      if (parent && !listed) properties._dm_addon_for = parent;

      if (listed) {
        // Sold as shown, the same line its own page adds. The parent stays off it too: on the bow
        // page that is the shopper's initials.
        properties._dm_template = 'dm-standard';
      } else if (this.dataset.mode === 'colors') {
        if (!this.#noName && this.#petName) {
          properties['Pet Name'] = this.#petName;
          properties._wroe_personalization_version = '2';
        }
        properties._dm_template = 'dm-personalized';
      } else if (item.dataset.personalizable === 'true') {
        // The same markers as the main line: this line is personalized the same way, through this page.
        if (this.#petName) {
          properties['Pet Name'] = this.#petName;
          properties._wroe_personalization_version = '2';
          properties._dm_template = 'dm-personalized';
        }
        const { fabric, thread } = this.#colorsFor(item);
        if (thread) {
          properties['Embroidery Color'] = thread.label;
          if (thread.swatch) properties._wroe_embroidery_color_swatch = thread.swatch;
        }
        if (fabric) {
          properties['Front Fabric'] = fabric.label;
          if (fabric.swatch) properties._wroe_front_fabric_swatch = fabric.swatch;
        }
      }

      items.push({
        id: Number(item.__variant.id),
        quantity: 1,
        properties,
        _title: listed ? this.#listLabel(item) : item.dataset.title || item.dataset.short || 'Item',
        _cardId: item.dataset.productId || item.dataset.color || '',
      });
    }

    return items;
  }

  getTotalCents() {
    let total = 0;
    for (const item of this.#items()) {
      if (item.__picked && item.__variant) total += Number(item.__variant.price) || 0;
    }
    return total;
  }

  /**
   * Turn a tile off after the cart rejected it, and say why.
   * @param {string} id
   * @param {string} message
   */
  rejectCard(id, message) {
    for (const item of this.#items()) {
      if ((item.dataset.productId || item.dataset.color) !== id) continue;
      item.__picked = false;
      this.#error(`${item.dataset.short}: ${message}`);
    }
    this.#render();
  }

  /** @param {string} message */
  #error(message) {
    const element = this.querySelector('[data-dm-room-error]');
    if (!(element instanceof HTMLElement)) return;
    element.hidden = !message;
    element.textContent = message;
  }

  #publish() {
    this.dispatchEvent(
      new CustomEvent('dm:addons-change', {
        detail: { totalCents: this.getTotalCents(), count: this.getItems().length },
        bubbles: true,
        composed: true,
      })
    );
  }

  /** @param {number} cents */
  #money(cents) {
    try {
      return formatMoney(cents, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
    } catch {
      return '';
    }
  }
}

if (!customElements.get('dm-room')) {
  customElements.define('dm-room', DmRoom);
}
